/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { GhostButton, PrimaryButton } from '../../../../styles/ui/Button';

import Modal from '../../../../components/Modal/Modal';
import RouteTypeToggle from './RouteTypeToggle';
import {
   mapFieldErrors,
   mapGeocodeRequiredItems,
   mapServiceClassificationItems,
   optimizeErrorCode,
} from '../../../../services/routing/routingScheduleApi';

// =============================================================================
// FleetOptimizeModal — modal de frota que dispara a otimização (handoff 0030 §1).
//
// O operador informa a frota (motos/carros + limites) → onSubmit dispara o POST
// .../optimize. Em sucesso (202) o PAI fecha o modal e inicia o polling. Em erro
// o modal renderiza a UX por código:
//   already_optimized → bloqueia o botão + orienta desfazer
//   geocode_required  → LISTA os itens sem coordenada (nunca silencioso)
//   validation        → erro por campo (fleet_params)
//   not_found         → "Nada a otimizar."
// =============================================================================

const Body = styled.div`
   display: flex;
   flex-direction: column;
   gap: 12px;
   font-size: 0.85rem;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
`;

const Intro = styled.p`
   margin: 0;
   font-size: 0.8rem;
   line-height: 1.5;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
`;

const WarnBox = styled.div`
   background: rgba(211, 47, 47, 0.08);
   border: 1px solid rgba(211, 47, 47, 0.4);
   border-radius: 8px;
   padding: 10px 12px;
   font-size: 0.8rem;
   color: #b71c1c;
   line-height: 1.5;
`;

const GeocodeBox = styled.div`
   background: rgba(245, 158, 11, 0.08);
   border: 1px solid rgba(245, 158, 11, 0.45);
   border-radius: 8px;
   padding: 10px 12px;
   font-size: 0.8rem;
   color: #92400e;
   line-height: 1.5;

   strong {
      display: block;
      margin-bottom: 6px;
   }

   ul {
      margin: 0;
      padding-left: 18px;
      max-height: 160px;
      overflow-y: auto;
   }

   li {
      margin-bottom: 2px;
   }
`;

const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;

   label {
      font-size: 0.75rem;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
   }

   input {
      padding: 8px 10px;
      font-size: 0.85rem;
      border-radius: 8px;
      border: 1px solid
         ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
      font-family: inherit;
   }
`;

const Grid = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 12px;
`;

const FieldError = styled.span`
   font-size: 0.7rem;
   color: #c62828;
`;

// Defaults do handoff §1 (exemplo). O operador ajusta conforme a frota do dia.
const DEFAULTS = {
   motorcycles: '3',
   maxStopsMotorcycle: '15',
   cars: '2',
   maxStopsCar: '20',
   maxRouteDurationMinutes: '240',
};

// Inteiro ≥ 0 a partir do input. Vazio/inválido → NaN (sinaliza erro).
function toInt(value) {
   const trimmed = String(value ?? '').trim();
   if (trimmed === '' || !/^\d+$/.test(trimmed)) return NaN;
   return Number(trimmed);
}

// Normaliza um TIME do backend ("HH:MM:SS" ou "HH:MM") para o valor "HH:MM"
// aceito pelo <input type="time">. Vazio/null → '' (campo em branco).
function toHHMM(value) {
   const trimmed = String(value ?? '').trim();
   return /^\d{2}:\d{2}/.test(trimmed) ? trimmed.slice(0, 5) : '';
}

export default function FleetOptimizeModal({
   onClose,
   onSubmit,
   defaultStartTime = null,
   defaultRouteType = 'round_trip',
   unclassifiedCount = 0,
   geocodeRequiredCount = 0,
}) {
   const [motorcycles, setMotorcycles] = useState(DEFAULTS.motorcycles);
   const [maxStopsMotorcycle, setMaxStopsMotorcycle] = useState(
      DEFAULTS.maxStopsMotorcycle
   );
   const [cars, setCars] = useState(DEFAULTS.cars);
   const [maxStopsCar, setMaxStopsCar] = useState(DEFAULTS.maxStopsCar);
   const [maxRouteDurationMinutes, setMaxRouteDurationMinutes] = useState(
      DEFAULTS.maxRouteDurationMinutes
   );
   // start_time (handoff 0034 §2) — saída da base (T0), ancora os ETAs.
   // Pré-preenchido com default_start_time da base; operador pode sobrescrever.
   const [startTime, setStartTime] = useState(() => toHHMM(defaultStartTime));
   // route_type (handoff 0057 §2) — default da roteirização inteira. Pré-
   // selecionado pelo pai (schedule.route_type quando já otimizado; senão o
   // default por período — TARDE nasce 'one_way'). Operador pode trocar.
   const [routeType, setRouteType] = useState(defaultRouteType);

   const [isSubmitting, setIsSubmitting] = useState(false);
   const [fieldErrors, setFieldErrors] = useState({});
   const [generalError, setGeneralError] = useState(null);
   const [geocodeItems, setGeocodeItems] = useState([]);
   // Itens sem serviço associado barrados pelo disparo (handoff 0037 §A.5).
   const [classificationItems, setClassificationItems] = useState([]);
   const [alreadyOptimized, setAlreadyOptimized] = useState(false);

   // Pré-aviso: há itens PENDENTES sem serviço (contagem vinda do board). A
   // otimização barra (422) enquanto houver — bloqueia o disparo de saída.
   const blockedByClassification = unclassifiedCount > 0;
   // Pré-aviso: há itens PENDENTES sem coordenada (handoff 0043). O guard
   // geocode_required barra a otimização — bloqueia o disparo de saída também.
   const blockedByGeocode = geocodeRequiredCount > 0;
   const blockedPreflight = blockedByClassification || blockedByGeocode;

   // Validação client-side antes do POST — espelha as regras do §1: todos
   // inteiros ≥ 0; max_stops/duration ≥ 1; motos + carros ≥ 1.
   const validate = () => {
      const errors = {};
      const m = toInt(motorcycles);
      const c = toInt(cars);
      const msm = toInt(maxStopsMotorcycle);
      const msc = toInt(maxStopsCar);
      const dur = toInt(maxRouteDurationMinutes);

      if (Number.isNaN(m)) errors.motorcycles = 'Informe um inteiro ≥ 0.';
      if (Number.isNaN(c)) errors.cars = 'Informe um inteiro ≥ 0.';
      if (Number.isNaN(msm) || msm < 1)
         errors.max_stops_motorcycle = 'Mín. 1 parada.';
      if (Number.isNaN(msc) || msc < 1)
         errors.max_stops_car = 'Mín. 1 parada.';
      if (Number.isNaN(dur) || dur < 1)
         errors.max_route_duration_minutes = 'Mín. 1 minuto.';
      if (!Number.isNaN(m) && !Number.isNaN(c) && m + c < 1)
         errors.motorcycles = 'A frota precisa de ao menos 1 veículo.';

      // start_time obrigatório (§2): "HH:MM". Vazio (inclusive quando a base não
      // tem default_start_time e o operador não digitou) → barra antes do POST.
      const st = String(startTime ?? '').trim();
      if (!/^\d{2}:\d{2}$/.test(st))
         errors.start_time = 'Informe o horário de saída da base (HH:MM).';

      return { errors, values: { m, c, msm, msc, dur, st } };
   };

   useEffect(() => {
      setFieldErrors({});
      setGeneralError(null);
      setGeocodeItems([]);
      setClassificationItems([]);
      setAlreadyOptimized(false);
   }, []);

   const handleConfirm = async () => {
      setFieldErrors({});
      setGeneralError(null);
      setGeocodeItems([]);
      setClassificationItems([]);
      setAlreadyOptimized(false);

      // Guard de saída: itens pendentes sem serviço barram a otimização (§A.5).
      if (blockedByClassification) {
         setGeneralError(
            `Há ${unclassifiedCount} item(ns) pendente(s) sem serviço atribuído. Atribua-os antes de otimizar.`
         );
         return;
      }

      // Guard de saída: itens pendentes sem coordenada barram a otimização (0043).
      if (blockedByGeocode) {
         setGeneralError(
            `Há ${geocodeRequiredCount} item(ns) pendente(s) sem localização. Defina o ponto de cada um (ficha da parada → “Definir localização”) antes de otimizar.`
         );
         return;
      }

      const { errors, values } = validate();
      if (Object.keys(errors).length > 0) {
         setFieldErrors(errors);
         return;
      }

      setIsSubmitting(true);
      try {
         await onSubmit(
            {
               motorcycles: values.m,
               max_stops_motorcycle: values.msm,
               cars: values.c,
               max_stops_car: values.msc,
               max_route_duration_minutes: values.dur,
            },
            values.st,
            routeType
         );
         // Sucesso (202): o pai fecha o modal e começa o polling.
      } catch (err) {
         const code = optimizeErrorCode(err);
         const apiMessage = err?.response?.data?.message;

         if (code === 'cluster_not_empty') {
            setGeneralError(
               apiMessage ??
                  'Há rotas nesta roteirização. Esvazie “Roteirizados” (remova as rotas) antes de otimizar.'
            );
         } else if (code === 'below_optimization_threshold') {
            setGeneralError(
               apiMessage ??
                  'São necessários ao menos 10 pedidos pendentes para otimizar.'
            );
         } else if (code === 'geocode_required') {
            setGeocodeItems(mapGeocodeRequiredItems(err));
            setGeneralError(
               apiMessage ??
                  'Há itens pendentes sem coordenada — geocodifique antes de otimizar.'
            );
         } else if (code === 'service_classification_required') {
            setClassificationItems(mapServiceClassificationItems(err));
            setGeneralError(
               apiMessage ??
                  'Há itens pendentes sem serviço atribuído — atribua antes de otimizar.'
            );
         } else {
            const mapped = mapFieldErrors(err);
            if (Object.keys(mapped).length > 0) {
               setFieldErrors(mapped);
            } else {
               setGeneralError(apiMessage ?? 'Não foi possível otimizar.');
            }
         }
         setIsSubmitting(false);
      }
   };

   return (
      <Modal
         isOpen
         onClose={isSubmitting ? () => {} : onClose}
         title="Otimizar roteirização"
         maxWidth="480px"
         footer={
            <>
               <GhostButton
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
               >
                  {alreadyOptimized ? 'Fechar' : 'Cancelar'}
               </GhostButton>
               {!alreadyOptimized && (
                  <PrimaryButton
                     type="button"
                     onClick={handleConfirm}
                     disabled={isSubmitting || blockedPreflight}
                     title={
                        // eslint-disable-next-line no-nested-ternary
                        blockedByClassification
                           ? 'Atribua o serviço dos itens pendentes antes de otimizar.'
                           : blockedByGeocode
                             ? 'Defina a localização dos itens pendentes antes de otimizar.'
                             : undefined
                     }
                  >
                     {isSubmitting ? 'Enviando…' : 'Otimizar'}
                  </PrimaryButton>
               )}
            </>
         }
      >
         <Body>
            <Intro>
               Informe a frota disponível. A otimização só roda com o{' '}
               <strong>cluster vazio</strong> (nenhuma rota criada) — depois,
               ajuste as rotas à mão. Para re-otimizar, esvazie “Roteirizados”
               (devolva os itens ao dock e remova as rotas) antes.
            </Intro>

            {alreadyOptimized && (
               <WarnBox>
                  Esta roteirização já foi otimizada. Para refazer, desfaça a
                  roteirização (recurso futuro) — por ora, ajuste as rotas
                  manualmente.
               </WarnBox>
            )}

            {blockedByClassification && !alreadyOptimized && (
               <GeocodeBox>
                  <strong>
                     {unclassifiedCount} item(ns) pendente(s) sem serviço:
                  </strong>
                  Atribua o serviço de cada item pendente (ficha da parada →
                  “Atribuir serviços”) antes de otimizar. A otimização usa o
                  tempo de atendimento para empacotar as rotas.
               </GeocodeBox>
            )}

            {blockedByGeocode && !alreadyOptimized && (
               <GeocodeBox>
                  <strong>
                     {geocodeRequiredCount} item(ns) pendente(s) sem localização:
                  </strong>
                  Defina o ponto de cada item sem coordenada (ficha da parada →
                  “Definir localização”) antes de otimizar. Itens sem coordenada
                  não entram no cálculo das rotas.
               </GeocodeBox>
            )}

            {classificationItems.length > 0 && (
               <GeocodeBox>
                  <strong>
                     Itens sem serviço ({classificationItems.length}):
                  </strong>
                  <ul>
                     {classificationItems.map((it) => (
                        <li key={it.field}>{it.message}</li>
                     ))}
                  </ul>
               </GeocodeBox>
            )}

            {generalError && !alreadyOptimized && (
               <WarnBox>{generalError}</WarnBox>
            )}

            {geocodeItems.length > 0 && (
               <GeocodeBox>
                  <strong>Itens sem coordenada ({geocodeItems.length}):</strong>
                  <ul>
                     {geocodeItems.map((it) => (
                        <li key={it.field}>{it.message}</li>
                     ))}
                  </ul>
               </GeocodeBox>
            )}

            {!alreadyOptimized && (
               <>
                  <Field>
                     <label htmlFor="fleet-start-time">
                        Horário de saída da base (T0)
                     </label>
                     <input
                        id="fleet-start-time"
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        disabled={isSubmitting}
                     />
                     {fieldErrors.start_time && (
                        <FieldError>{fieldErrors.start_time}</FieldError>
                     )}
                  </Field>

                  <RouteTypeToggle
                     name="fleet-route-type"
                     value={routeType}
                     onChange={setRouteType}
                     disabled={isSubmitting}
                  />

                  <Grid>
                     <Field>
                        <label htmlFor="fleet-motos">Motos</label>
                        <input
                           id="fleet-motos"
                           type="number"
                           min="0"
                           value={motorcycles}
                           onChange={(e) => setMotorcycles(e.target.value)}
                           disabled={isSubmitting}
                        />
                        {fieldErrors.motorcycles && (
                           <FieldError>{fieldErrors.motorcycles}</FieldError>
                        )}
                     </Field>
                     <Field>
                        <label htmlFor="fleet-motos-stops">
                           Máx. paradas / moto
                        </label>
                        <input
                           id="fleet-motos-stops"
                           type="number"
                           min="1"
                           value={maxStopsMotorcycle}
                           onChange={(e) =>
                              setMaxStopsMotorcycle(e.target.value)
                           }
                           disabled={isSubmitting}
                        />
                        {fieldErrors.max_stops_motorcycle && (
                           <FieldError>
                              {fieldErrors.max_stops_motorcycle}
                           </FieldError>
                        )}
                     </Field>
                  </Grid>

                  <Grid>
                     <Field>
                        <label htmlFor="fleet-cars">Carros</label>
                        <input
                           id="fleet-cars"
                           type="number"
                           min="0"
                           value={cars}
                           onChange={(e) => setCars(e.target.value)}
                           disabled={isSubmitting}
                        />
                        {fieldErrors.cars && (
                           <FieldError>{fieldErrors.cars}</FieldError>
                        )}
                     </Field>
                     <Field>
                        <label htmlFor="fleet-cars-stops">
                           Máx. paradas / carro
                        </label>
                        <input
                           id="fleet-cars-stops"
                           type="number"
                           min="1"
                           value={maxStopsCar}
                           onChange={(e) => setMaxStopsCar(e.target.value)}
                           disabled={isSubmitting}
                        />
                        {fieldErrors.max_stops_car && (
                           <FieldError>{fieldErrors.max_stops_car}</FieldError>
                        )}
                     </Field>
                  </Grid>

                  <Field>
                     <label htmlFor="fleet-duration">
                        Tempo máx. por rota (min)
                     </label>
                     <input
                        id="fleet-duration"
                        type="number"
                        min="1"
                        value={maxRouteDurationMinutes}
                        onChange={(e) =>
                           setMaxRouteDurationMinutes(e.target.value)
                        }
                        disabled={isSubmitting}
                     />
                     {fieldErrors.max_route_duration_minutes && (
                        <FieldError>
                           {fieldErrors.max_route_duration_minutes}
                        </FieldError>
                     )}
                  </Field>
               </>
            )}
         </Body>
      </Modal>
   );
}

FleetOptimizeModal.propTypes = {
   onClose: PropTypes.func.isRequired,
   // async (fleetParams, startTime, routeType) → resolve em 202; rejeita com o
   // erro do axios.
   onSubmit: PropTypes.func.isRequired,
   // default_start_time da base ("HH:MM:SS"|"HH:MM"|null) — pré-preenche o campo.
   defaultStartTime: PropTypes.string,
   // Pré-seleção do route_type (handoff 0057): schedule.route_type quando já
   // otimizado, senão o default por período resolvido pelo pai.
   defaultRouteType: PropTypes.oneOf(['round_trip', 'one_way']),
   // Itens PENDENTES sem serviço classificado (handoff 0037 §A.5) — bloqueia
   // o disparo de saída e exibe o pré-aviso.
   unclassifiedCount: PropTypes.number,
   // Itens PENDENTES sem coordenada (handoff 0043) — bloqueia o disparo de
   // saída e exibe o pré-aviso (guard geocode_required do backend).
   geocodeRequiredCount: PropTypes.number,
};
